import { ColorFilter } from "./ColorFinderPanel"

declare global {
    interface HTMLVideoElement {
        captureStream(): MediaStream
    }
    type MediaStreamVideoTrack = MediaStreamTrack
}

export class WebGPURenderer {
    private canvas: HTMLCanvasElement
    private device?: GPUDevice
    private context?: GPUCanvasContext

    private pAnalysis?: GPUComputePipeline
    private pFilter?: GPUComputePipeline
    private pVideo?: GPURenderPipeline
    private pOverlay?: GPURenderPipeline

    private lut?: GPUBuffer
    private gridRaw?: GPUBuffer
    private gridFinal?: GPUBuffer
    private uniforms?: GPUBuffer
    private sampler?: GPUSampler

    private w: number = 0
    private h: number = 0

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas
    }

    public async initialize(): Promise<void> {
        if (!navigator.gpu) throw new Error("WebGPU not supported.")
        const adapter = await navigator.gpu.requestAdapter()
        if (!adapter) throw new Error("No Adapter.")
        this.device = await adapter.requestDevice({
            requiredLimits: {
                maxTextureDimension2D: Math.min(adapter.limits.maxTextureDimension2D, 16384),
            },
        })

        this.context = this.canvas.getContext("webgpu") as GPUCanvasContext
        this.context.configure({
            device: this.device,
            format: navigator.gpu.getPreferredCanvasFormat(),
            alphaMode: "opaque",
        })

        const commonCode = `
            struct Uniforms { w: u32, h: u32 };
            @group(0) @binding(0) var<uniform> size: Uniforms;
            
            fn is_red(c: vec4<f32>, lut_ptr: ptr<storage, array<u32>, read>) -> bool {
                let u = vec4<u32>(c * 255.0);
                let idx = (u.r >> 3u) * 1024u + (u.g >> 3u) * 32u + (u.b >> 3u);
                return ((*lut_ptr)[idx >> 5u] & (1u << (idx & 31u))) != 0u;
            }
        `

        // --- COMPUTE MODULE ---
        const computeModule = this.device.createShaderModule({
            code: `
            ${commonCode}
            @group(0) @binding(1) var tex: texture_external;
            @group(0) @binding(2) var<storage, read> lut: array<u32>;
            @group(0) @binding(3) var<storage, read_write> grid_raw: array<u32>;
            @group(0) @binding(4) var<storage, read_write> grid_final: array<u32>;

            // RENAMED 'active' to 'wg_active' because 'active' is reserved
            var<workgroup> wg_active: atomic<u32>;

            @compute @workgroup_size(16, 16)
            fn analysis_main(@builtin(global_invocation_id) id: vec3<u32>, @builtin(local_invocation_id) local: vec3<u32>, @builtin(workgroup_id) group: vec3<u32>) {
                if (local.x == 0u && local.y == 0u) { atomicStore(&wg_active, 0u); }
                workgroupBarrier();
                
                // Force usage of size to prevent optimization
                let safe_w = size.w; 

                if (id.x < textureDimensions(tex).x && id.y < textureDimensions(tex).y) {
                    if (is_red(textureLoad(tex, id.xy), &lut)) { atomicStore(&wg_active, 1u); }
                }
                workgroupBarrier();

                if (local.x == 0u && local.y == 0u) {
                    grid_raw[group.y * size.w + group.x] = atomicLoad(&wg_active);
                }
            }

            @compute @workgroup_size(64)
            fn filter_main(@builtin(global_invocation_id) id: vec3<u32>) {
                let idx = id.x;
                if (idx >= size.w * size.h) { return; }
                if (grid_raw[idx] == 0u) { grid_final[idx] = 0u; return; }

                var count = 0;
                let cx = i32(idx % size.w);
                let cy = i32(idx / size.w);
                
                for (var y = -4; y <= 4; y++) {
                    for (var x = -4; x <= 4; x++) {
                        let nx = cx + x; let ny = cy + y;
                        if (nx >= 0 && nx < i32(size.w) && ny >= 0 && ny < i32(size.h)) {
                            if (grid_raw[u32(ny) * size.w + u32(nx)] == 1u) { count++; }
                        }
                    }
                }
                grid_final[idx] = select(1u, 0u, count > 20); 
            }
            `,
        })

        // --- RENDER MODULE ---
        const renderModule = this.device.createShaderModule({
            code: `
            ${commonCode}
            @group(0) @binding(1) var tex: texture_external;
            @group(0) @binding(2) var samp: sampler;
            @group(0) @binding(3) var<storage, read> lut: array<u32>;
            @group(0) @binding(4) var<storage, read> grid_final: array<u32>;

            struct VOut { @builtin(position) pos: vec4<f32>, @location(0) uv: vec2<f32> };
            
            @vertex fn vs_video(@builtin(vertex_index) i: u32) -> VOut {
                var pos = vec2<f32>(f32(i/2u), f32(i%2u)) * 4.0 - 1.0;
                return VOut(vec4<f32>(pos.x, -pos.y, 0.0, 1.0), (pos + 1.0) * 0.5);
            }

            @fragment fn fs_video(@location(0) uv: vec2<f32>) -> @location(0) vec4<f32> {
                let c = textureSampleBaseClampToEdge(tex, samp, uv);
                if (is_red(c, &lut)) { return c; }
                return vec4<f32>(c.rgb * 0.15, c.a);
            }

            struct InstOut { @builtin(position) pos: vec4<f32>, @location(0) uv: vec2<f32>, @location(1) @interpolate(flat) idx: u32 };
            
            @vertex fn vs_overlay(@builtin(vertex_index) v: u32, @builtin(instance_index) i: u32) -> InstOut {
                if (grid_final[i] == 1u) { return InstOut(vec4<f32>(-2.0), vec2<f32>(0.0), i); }
                let tile = vec2<f32>(f32(i % size.w), f32(i / size.w));
                let quad = vec2<f32>(f32(v % 2u), f32(v / 2u));
                let screen = ((tile + quad) / vec2<f32>(f32(size.w), f32(size.h))) * 2.0 - 1.0;
                return InstOut(vec4<f32>(screen.x, -screen.y, 0.0, 1.0), quad, i);
            }

            @fragment fn fs_overlay(@location(0) uv: vec2<f32>, @location(1) @interpolate(flat) idx: u32) -> @location(0) vec4<f32> {
                let px = uv * 16.0;
                
                let f = fwidth(px);
                let fw = f.x + f.y; 

                let line_width = 2.0;

                var d = 999.0;
                let w = size.w; let h = size.h;
                let cx = i32(idx % w); let cy = i32(idx / w);
                
                for(var y=-1; y<=1; y++){ for(var x=-1; x<=1; x++){
                    if (x==0 && y==0) { continue; }
                    let nx = cx + x; let ny = cy + y;
                    if (nx>=0 && nx<i32(w) && ny>=0 && ny<i32(h) && grid_final[u32(ny)*w + u32(nx)] == 1u) {
                            let dist_box = max(select(0.0, 16.0-px.x, x==1) + select(0.0, px.x, x==-1),
                                            select(0.0, 16.0-px.y, y==1) + select(0.0, px.y, y==-1));
                            d = min(d, dist_box);
                    }
                }}

                if (d >= 5.0 && d <= 5.0 + line_width) { return vec4<f32>(1.0, 0.0, 0.0, 1.0); }
                
                discard; return vec4<f32>(0.0);
            }
            `,
        })

        this.pAnalysis = this.device.createComputePipeline({
            layout: "auto",
            compute: { module: computeModule, entryPoint: "analysis_main" },
        })
        this.pFilter = this.device.createComputePipeline({
            layout: "auto",
            compute: { module: computeModule, entryPoint: "filter_main" },
        })

        const format = navigator.gpu.getPreferredCanvasFormat()
        const blend = {
            color: {
                srcFactor: "src-alpha",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
            } as const,
            alpha: {
                srcFactor: "one",
                dstFactor: "one-minus-src-alpha",
                operation: "add",
            } as const,
        }

        this.pVideo = this.device.createRenderPipeline({
            layout: "auto",
            vertex: { module: renderModule, entryPoint: "vs_video" },
            fragment: { module: renderModule, entryPoint: "fs_video", targets: [{ format }] },
            primitive: { topology: "triangle-strip" },
        })

        this.pOverlay = this.device.createRenderPipeline({
            layout: "auto",
            vertex: { module: renderModule, entryPoint: "vs_overlay" },
            fragment: {
                module: renderModule,
                entryPoint: "fs_overlay",
                targets: [{ format, blend }],
            },
            primitive: { topology: "triangle-strip" },
        })

        this.lut = this.device.createBuffer({
            size: 4096,
            usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
        })
        this.device.queue.writeBuffer(this.lut, 0, new Uint32Array(1024).fill(0xffffffff))
        this.uniforms = this.device.createBuffer({
            size: 8,
            usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        })
        this.sampler = this.device.createSampler({
            magFilter: "nearest",
            minFilter: "nearest",
        })
    }

    public renderVideoFrame(source: VideoFrame | HTMLVideoElement): void {
        if (!this.device || !this.pAnalysis) return
        const w = source instanceof VideoFrame ? source.displayWidth : source.videoWidth
        const h = source instanceof VideoFrame ? source.displayHeight : source.videoHeight

        if (this.w !== Math.ceil(w / 16) || this.h !== Math.ceil(h / 16)) {
            this.w = Math.ceil(w / 16)
            this.h = Math.ceil(h / 16)
            this.device.queue.writeBuffer(this.uniforms!, 0, new Uint32Array([this.w, this.h]))
            const size = this.w * this.h * 4
            if (this.gridRaw) this.gridRaw.destroy()
            if (this.gridFinal) this.gridFinal.destroy()
            this.gridRaw = this.device.createBuffer({ size, usage: GPUBufferUsage.STORAGE })
            this.gridFinal = this.device.createBuffer({ size, usage: GPUBufferUsage.STORAGE })
        }

        const tex = this.device.importExternalTexture({ source })
        const enc = this.device.createCommandEncoder()

        // 1. ANALYSIS: Uses Uniforms(0), Tex(1), Lut(2), GridRaw(3)
        const bgAnalysis = this.device.createBindGroup({
            layout: this.pAnalysis!.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: this.uniforms! } },
                { binding: 1, resource: tex },
                { binding: 2, resource: { buffer: this.lut! } },
                { binding: 3, resource: { buffer: this.gridRaw! } },
            ],
        })

        // 2. FILTER: Uses Uniforms(0), GridRaw(3), GridFinal(4)
        const bgFilter = this.device.createBindGroup({
            layout: this.pFilter!.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: this.uniforms! } },
                { binding: 3, resource: { buffer: this.gridRaw! } },
                { binding: 4, resource: { buffer: this.gridFinal! } },
            ],
        })

        // 3. VIDEO RENDER: Uses Tex(1), Sampler(2), Lut(3)
        const bgVideo = this.device.createBindGroup({
            layout: this.pVideo!.getBindGroupLayout(0),
            entries: [
                { binding: 1, resource: tex },
                { binding: 2, resource: this.sampler! },
                { binding: 3, resource: { buffer: this.lut! } },
            ],
        })

        // 4. OVERLAY RENDER: Uses Uniforms(0), GridFinal(4)
        const bgOverlay = this.device.createBindGroup({
            layout: this.pOverlay!.getBindGroupLayout(0),
            entries: [
                { binding: 0, resource: { buffer: this.uniforms! } },
                { binding: 4, resource: { buffer: this.gridFinal! } },
            ],
        })

        const c1 = enc.beginComputePass()
        c1.setPipeline(this.pAnalysis!)
        c1.setBindGroup(0, bgAnalysis)
        c1.dispatchWorkgroups(this.w, this.h)
        c1.end()

        const c2 = enc.beginComputePass()
        c2.setPipeline(this.pFilter!)
        c2.setBindGroup(0, bgFilter)
        c2.dispatchWorkgroups(Math.ceil((this.w * this.h) / 64))
        c2.end()

        const r = enc.beginRenderPass({
            colorAttachments: [
                {
                    view: this.context!.getCurrentTexture().createView(),
                    loadOp: "clear",
                    storeOp: "store",
                    clearValue: { r: 0, g: 0, b: 0, a: 1 },
                },
            ],
        })
        r.setPipeline(this.pVideo!)
        r.setBindGroup(0, bgVideo)
        r.draw(4)
        r.setPipeline(this.pOverlay!)
        r.setBindGroup(0, bgOverlay)
        r.draw(4, this.w * this.h)
        r.end()

        this.device.queue.submit([enc.finish()])
    }

    public setColorFilter(filter?: ColorFilter): void {
        if (!this.device || !this.lut) return
        const d = new Uint32Array(1024)
        if (filter?.size) {
            d.fill(0)
            filter.forEach((i) => (d[i >> 5] |= 1 << (i & 31)))
        } else {
            d.fill(0xffffffff)
        }
        this.device.queue.writeBuffer(this.lut, 0, d)
    }
}
